import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "../../lib/dayjs";
import { listMyMedicalRecords } from "../../services/medicalRecords.service";
import { IMedicalRecordListItem } from "../../interfaces/IMedicalRecord";
import { ROUTES } from "../../router/constants";

const SPECIALTY_PT: Record<string, string> = {
  OPHTHALMOLOGY: "Oftalmológica",
};

function recordTitle(r: IMedicalRecordListItem): string {
  const specialty = r.specialty ? (SPECIALTY_PT[r.specialty] ?? r.specialty) : "Geral";
  const date = dayjs(r.createdAt).format("DD/MM/YYYY");
  return `Consulta ${specialty} — ${date}`;
}

export function MyMedicalRecords() {
  const navigate = useNavigate();

  const [records, setRecords] = useState<IMedicalRecordListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const LIMIT = 10;
  const hasMore = records.length < total;

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        setLoading(true);
        const result = await listMyMedicalRecords({ page: 1, limit: LIMIT });
        if (ignore) return;
        setRecords(result.data);
        setTotal(result.meta.total);
        setPage(1);
      } catch {
        if (!ignore) toast.error("Não foi possível carregar seus prontuários.");
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, []);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const result = await listMyMedicalRecords({ page: nextPage, limit: LIMIT });
      setRecords((prev) => [...prev, ...result.data]);
      setPage(nextPage);
    } catch {
      toast.error("Não foi possível carregar mais registros.");
    } finally {
      setLoadingMore(false);
    }
  };

  const handleOpenRecord = (id: string) => navigate(`/prontuario/${id}`);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "flex-start" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#133859">
            Meus Prontuários
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? "Carregando..." : `${total} ${total === 1 ? "registro encontrado" : "registros encontrados"}`}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ContentPasteOutlinedIcon />}
          onClick={() => navigate(ROUTES.PRESCRIPTION_TEMPLATES)}
          sx={{ borderColor: "#133859", color: "#133859" }}
          size="small"
        >
          Gerenciar modelos
        </Button>
      </Stack>

      {/* loading */}
      {loading && (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent>
                <Stack spacing={1}>
                  <Skeleton variant="text" width={220} height={24} />
                  <Skeleton variant="text" width={140} />
                  <Skeleton variant="text" width="80%" />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* vazio */}
      {!loading && records.length === 0 && (
        <Card>
          <CardContent>
            <Typography align="center" color="text.secondary">
              Nenhum prontuário registrado ainda.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* lista */}
      {!loading && records.length > 0 && (
        <Stack spacing={2}>
          {records.map((r) => (
            <Card
              key={r.id}
              sx={{ cursor: "pointer", "&:hover": { boxShadow: 4 }, transition: "box-shadow .2s" }}
              onClick={() => handleOpenRecord(r.id)}
            >
              <CardContent>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-start" }} spacing={1}>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight={700} color="#133859" sx={{ mb: 0.5 }}>
                      {recordTitle(r)}
                    </Typography>

                    {r.chiefComplaint && (
                      <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                        <strong>Queixa:</strong> {r.chiefComplaint}
                      </Typography>
                    )}

                    {r.diagnosis && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        <strong>Diagnóstico:</strong> {r.diagnosis}
                      </Typography>
                    )}

                    {r.cidCodes?.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {r.cidCodes.map((c) => (
                          <Chip key={c} label={c} size="small" color="default" />
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={0.5} sx={{ minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">
                      Paciente: {r.patient?.name ?? "—"}
                    </Typography>
                    {r.returnDate && (
                      <Typography variant="caption" color="primary">
                        Retorno: {dayjs(r.returnDate).format("DD/MM/YYYY")}
                      </Typography>
                    )}
                    <Button size="small" variant="outlined" sx={{ mt: 0.5, borderColor: "#133859", color: "#133859" }}>
                      Ver prontuário
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}

          {hasMore && (
            <Box textAlign="center" pt={1}>
              <Button onClick={handleLoadMore} disabled={loadingMore} variant="outlined">
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
}
